import { ArrowRight, Shield, Clock, CheckCircle, Truck, Wrench, Trash2, Package, Star, MapPin, Phone, ChevronDown, ClipboardList, PackageCheck, HelpCircle, ExternalLink, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { SEOHead } from '../components/SEOHead';
import { CTABanner } from '../components/CTABanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://panthera-umzug.de';

const schema = {
  "@context": "https://schema.org", "@type": "MovingCompany",
  "name": "Panthera Umzug & Montage", "url": BASE_URL, "logo": `${BASE_URL}/logo.png`,
  "telephone": "+491631945793", "email": "info@panthera-umzug.de",
  "address": { "@type": "PostalAddress", "addressLocality": "Berlin", "addressRegion": "Berlin", "addressCountry": "DE" },
  "geo": { "@type": "GeoCoordinates", "latitude": 52.52, "longitude": 13.405 },
  "areaServed": [{ "@type": "Country", "name": "Germany" }, { "@type": "City", "name": "Berlin" }, { "@type": "City", "name": "Hamburg" }, { "@type": "City", "name": "München" }, { "@type": "City", "name": "Köln" }, { "@type": "City", "name": "Frankfurt" }, { "@type": "City", "name": "Stuttgart" }, { "@type": "City", "name": "Düsseldorf" }, { "@type": "City", "name": "Leipzig" }, { "@type": "City", "name": "Dresden" }],
  "priceRange": "$$",
  "sameAs": ["https://www.facebook.com/share/1G9kWPwhQZ/?mibextid=wwXIfr", "https://www.instagram.com/panthera.umzug.berlin", "https://www.reddit.com/u/Panthera-umzug"],
  "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "08:00", "closes": "18:00" }
};

const serviceIcons = [Truck, Wrench, Trash2, Package];
const serviceLinks = ['/umzug-berlin', '/moebelmontage-berlin', '/entruempelung-berlin', '/kontakt'];
const benefitIcons = [Shield, Star, Clock, CheckCircle];
const processIcons = [Phone, ClipboardList, Truck, PackageCheck];
const districts = ['Charlottenburg', 'Kreuzberg', 'Neukölln', 'Mitte', 'Friedrichshain', 'Schöneberg', 'Prenzlauer Berg', 'Tempelhof', 'Steglitz', 'Spandau', 'Lichtenberg', 'Pankow'];
const germanCities = ['Hamburg', 'München', 'Köln', 'Frankfurt', 'Stuttgart', 'Düsseldorf', 'Leipzig', 'Dresden', 'Hannover', 'Nürnberg', 'Bremen', 'Dortmund'];

const galleryImages = [
  'https://customer-assets.emergentagent.com/job_16d9819a-ba16-49dd-ad0f-22efc5459f8f/artifacts/c712dgsp_62738FE5-7905-41C8-95E6-21C3EF40C4C6.png',
  'https://customer-assets.emergentagent.com/job_16d9819a-ba16-49dd-ad0f-22efc5459f8f/artifacts/albrmfc5_2066E48D-BEF3-4965-95BD-BD2C27B23183.png',
  'https://customer-assets.emergentagent.com/job_16d9819a-ba16-49dd-ad0f-22efc5459f8f/artifacts/v5ier0gw_IMG_6453.jpeg',
  'https://customer-assets.emergentagent.com/job_16d9819a-ba16-49dd-ad0f-22efc5459f8f/artifacts/btzke0wh_IMG_6665.jpeg',
];

const GOOGLE_MAPS_URL = 'https://maps.app.goo.gl/RfyNSN1eHsYsmJkB9';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#2a2a2a] rounded-xl overflow-hidden transition-all duration-300 hover:border-[#D4AF37]/30">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-[#111] hover:bg-[#1a1a1a] transition-colors">
        <span className="text-white font-medium pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-6 py-5 bg-[#0a0a0a] border-t border-[#2a2a2a]">
          <p className="text-gray-400 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const { t } = useLanguage();

  return (
    <>
      <SEOHead title="Umzugsunternehmen Berlin | Panthera Umzug & Möbelmontage" description="Professionelles Umzugsunternehmen in Berlin für Umzug, Möbelmontage und Entrümpelung. Jetzt unverbindliches Angebot anfordern." canonical={BASE_URL} schemaMarkup={schema} />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-black" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#1a1a1a] to-black opacity-90" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#D4AF37] rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#8F6B1F] rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        <div className="container mx-auto px-4 py-32 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 mb-8 bg-gradient-to-r from-[#D4AF37]/10 to-[#8F6B1F]/10 border border-[#D4AF37]/30 rounded-full">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-semibold">{t.hero.badge}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight" data-testid="homepage-h1">
              <span className="text-white">{t.hero.h1_1}</span><br />
              <span className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] bg-clip-text text-transparent">{t.hero.h1_2}</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">{t.hero.subtitle}</p>
            <div className="flex flex-wrap items-center justify-center gap-6 mb-10 text-gray-300">
              <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#D4AF37]" /><span>{t.hero.badge1}</span></div>
              <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#D4AF37]" /><span>{t.hero.badge2}</span></div>
              <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#D4AF37]" /><span>{t.hero.badge3}</span></div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link to="/kontakt">
                <Button size="lg" data-testid="hero-cta-btn" className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] hover:from-[#D4AF37] hover:to-[#8F6B1F] text-black font-bold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:scale-105 shadow-xl shadow-[#D4AF37]/30">
                  {t.hero.cta1} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+491631945793">
                <Button size="lg" variant="outline" data-testid="hero-call-btn" className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-bold px-10 py-7 text-lg rounded-lg transition-all duration-300 hover:scale-105">
                  <Phone className="mr-2 w-5 h-5" /> {t.hero.cta2}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="intro-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">{t.intro.title}</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>{t.intro.p1}</p>
              <p>{t.intro.p2}</p>
              <p>{t.intro.p3}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-black" id="services" data-testid="services-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="text-white">{t.services.title_1} </span>
              <span className="bg-gradient-to-r from-[#F1D27A] via-[#D4AF37] to-[#8F6B1F] bg-clip-text text-transparent">{t.services.title_2}</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">{t.services.subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.services.items.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <Link to={serviceLinks[i]} key={i}>
                  <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-[#2a2a2a] hover:border-[#D4AF37]/50 transition-all duration-300 hover:scale-[1.03] hover:shadow-xl hover:shadow-[#D4AF37]/10 h-full group" data-testid={`service-card-${i}`}>
                    <CardHeader>
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <CardTitle className="text-xl text-white group-hover:text-[#D4AF37] transition-colors">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                      <span className="inline-flex items-center gap-1 mt-4 text-[#D4AF37] text-sm font-medium">{t.services.more} <ArrowRight className="w-4 h-4" /></span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="benefits-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">{t.benefits.title}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {t.benefits.items.map((b, i) => {
              const Icon = benefitIcons[i];
              return (
                <div key={i} className="text-center p-6" data-testid={`benefit-${i}`}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#8F6B1F]/20 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{b.title}</h3>
                  <p className="text-gray-400 text-sm">{b.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="max-w-4xl mx-auto mt-12 space-y-4 text-gray-300 leading-relaxed">
            <p>{t.benefits.p1}</p>
            <p>{t.benefits.p2}</p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-20 bg-black" data-testid="gallery-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Camera className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">Panthera Umzug</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.gallery.title}</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">{t.gallery.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {galleryImages.map((src, i) => (
              <div key={i} className="group relative overflow-hidden rounded-xl border border-[#2a2a2a] hover:border-[#D4AF37]/40 transition-all duration-500" data-testid={`gallery-img-${i}`}>
                <div className="aspect-square overflow-hidden">
                  <img
                    src={src}
                    alt={t.gallery.items[i]?.alt || 'Panthera Umzug'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-white text-sm font-medium">{t.gallery.items[i]?.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="reviews-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.reviews.title}</h2>
            <p className="text-lg text-gray-400 mb-8">{t.reviews.subtitle}</p>
            {/* Google Rating Badge */}
            <div className="inline-flex items-center gap-4 px-8 py-4 bg-[#111] border border-[#2a2a2a] rounded-2xl mb-10">
              <div className="flex items-center gap-1">
                <span className="text-4xl font-bold text-white">{t.reviews.rating}</span>
              </div>
              <div className="text-left">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FBBC04] text-[#FBBC04]" />
                  ))}
                </div>
                <span className="text-gray-400 text-sm">{t.reviews.ratingText}</span>
              </div>
              <div className="w-px h-10 bg-[#2a2a2a]" />
              <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_light_color_74x24dp.png" alt="Google" className="h-5 opacity-80" loading="lazy" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto mb-10">
            {t.reviews.items.map((review, i) => (
              <Card key={i} className="bg-[#111] border-[#2a2a2a] hover:border-[#D4AF37]/30 transition-all duration-300" data-testid={`review-card-${i}`}>
                <CardContent className="pt-6 pb-5">
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(review.stars)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]" />
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">"{review.text}"</p>
                  <div className="flex items-center justify-between pt-3 border-t border-[#2a2a2a]">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37]/30 to-[#8F6B1F]/30 flex items-center justify-center">
                        <span className="text-[#D4AF37] text-xs font-bold">{review.name.charAt(0)}</span>
                      </div>
                      <span className="text-white text-sm font-medium">{review.name}</span>
                    </div>
                    <span className="text-gray-500 text-xs">{review.date}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" data-testid="google-reviews-link" className="border-[#2a2a2a] text-gray-300 hover:text-white hover:border-[#D4AF37]/50 px-6 py-5 rounded-lg transition-all">
                <ExternalLink className="mr-2 w-4 h-4" />
                {t.reviews.allReviews}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Cost */}
      <section className="py-20 bg-black" data-testid="cost-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">{t.cost.title}</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>{t.cost.p1}</p>
              <p>{t.cost.p2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="process-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.process.title}</h2>
            <p className="text-lg text-gray-400">{t.process.subtitle}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {t.process.steps.map((s, i) => {
              const Icon = processIcons[i];
              return (
                <div key={i} className="relative p-6 bg-[#111] rounded-xl border border-[#2a2a2a] hover:border-[#D4AF37]/30 transition-colors" data-testid={`process-step-${i}`}>
                  <div className="absolute -top-3 -left-3 w-9 h-9 bg-gradient-to-br from-[#F1D27A] to-[#D4AF37] rounded-full flex items-center justify-center text-black font-bold text-sm shadow-lg">{i + 1}</div>
                  <Icon className="w-8 h-8 text-[#D4AF37] mb-4 mt-2" />
                  <h3 className="text-white font-bold mb-2">{s.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-20 bg-[#0a0a0a]" data-testid="districts-section">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">{t.districts.title}</h2>
            <p className="text-lg text-gray-400 mb-10">{t.districts.subtitle}</p>

            {/* Berlin Districts */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Berlin</h3>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {districts.map((d, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-full text-gray-300 text-xs hover:border-[#D4AF37]/50 transition-colors">
                    <MapPin className="w-3 h-3 text-[#D4AF37]" />{d}
                  </span>
                ))}
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-3xl mx-auto">{t.districts.descBerlin}</p>
            </div>

            {/* Germany Cities */}
            <div className="mt-10">
              <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">Deutschland</h3>
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {germanCities.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-br from-[#D4AF37]/5 to-[#8F6B1F]/5 border border-[#D4AF37]/20 rounded-full text-gray-200 text-sm hover:border-[#D4AF37]/50 transition-colors">
                    <Truck className="w-3.5 h-3.5 text-[#D4AF37]" />{c}
                  </span>
                ))}
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-3xl mx-auto">{t.districts.descDE}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-black" data-testid="faq-section">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                <HelpCircle className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium">FAQ</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.faq.title}</h2>
              <p className="text-lg text-gray-400">{t.faq.subtitle}</p>
            </div>
            <div className="space-y-3">
              {t.faq.items.map((item, i) => (
                <FAQItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
